import { getPacksForFrameworks } from '@/packs/project-packs.js';

export interface FileCheckMapping {
  glob: string;
  checks: string[];
}

export interface NarrowedScope {
  checks_to_run: Set<string>;
  files_in_scope: string[];
  no_security_changes: boolean;
}

const GENERIC_SECURITY_MAP: FileCheckMapping[] = [
  {
    glob: '**/auth*',
    checks: ['permission-boundary-review', 'runtime-surface-probing', 'auth-mechanism-review'],
  },
  { glob: '**/guard*', checks: ['permission-boundary-review'] },
  { glob: '**/.env*', checks: ['runtime-surface-probing', 'cryptographic-review'] },
  { glob: '**/config*', checks: ['runtime-surface-probing'] },
  // Auth / session files
  { glob: '**/jwt*', checks: ['auth-mechanism-review'] },
  { glob: '**/token*', checks: ['auth-mechanism-review'] },
  { glob: '**/passport*', checks: ['auth-mechanism-review'] },
  { glob: '**/session*', checks: ['auth-mechanism-review'] },
  // GraphQL schema and resolvers
  { glob: '**/*.graphql', checks: ['input-validation-review', 'permission-boundary-review'] },
  { glob: '**/schema.graphql', checks: ['input-validation-review', 'permission-boundary-review'] },
  { glob: '**/resolvers/**', checks: ['input-validation-review', 'permission-boundary-review'] },
  // Logging and audit
  { glob: '**/log*', checks: ['logging-monitoring-review'] },
  { glob: '**/audit*', checks: ['logging-monitoring-review'] },
  { glob: '**/monitor*', checks: ['logging-monitoring-review'] },
  // Cryptography
  { glob: '**/*crypt*', checks: ['cryptographic-review'] },
  { glob: '**/*cipher*', checks: ['cryptographic-review'] },
  { glob: '**/*hash*', checks: ['cryptographic-review'] },
  // File uploads and input
  { glob: '**/upload*', checks: ['input-validation-review'] },
  { glob: '**/file*', checks: ['input-validation-review'] },
];

export class FileCheckMapper {
  constructor(
    private readonly frameworks: string[],
    private readonly projectRoot?: string,
  ) {}

  map(changedFiles: string[]): NarrowedScope {
    const mappings = this.getMappingsForStack();
    const checks_to_run = new Set<string>();
    const files_in_scope: string[] = [];

    for (const file of changedFiles) {
      for (const mapping of mappings) {
        if (this.matchesGlob(file, mapping.glob)) {
          for (const check of mapping.checks) {
            checks_to_run.add(check);
          }
          if (!files_in_scope.includes(file)) {
            files_in_scope.push(file);
          }
        }
      }
    }

    return {
      checks_to_run,
      files_in_scope,
      no_security_changes: files_in_scope.length === 0,
    };
  }

  private getMappingsForStack(): FileCheckMapping[] {
    const merged = new Map<string, Set<string>>();

    for (const mapping of GENERIC_SECURITY_MAP) {
      merged.set(mapping.glob, new Set(mapping.checks));
    }

    for (const pack of getPacksForFrameworks(this.frameworks, this.projectRoot)) {
      for (const mapping of pack.manifest.pentest?.file_check_map ?? []) {
        const checks = merged.get(mapping.glob) ?? new Set<string>();
        for (const check of mapping.checks) {
          checks.add(check);
        }
        merged.set(mapping.glob, checks);
      }
    }

    return Array.from(merged.entries()).map(([glob, checks]) => ({
      glob,
      checks: Array.from(checks),
    }));
  }

  private matchesGlob(file: string, pattern: string): boolean {
    const regexStr = pattern
      .replace(/\./g, '\\.')
      .replace(/\*\*/g, '<<<DOUBLESTAR>>>')
      .replace(/\*/g, '[^/]*')
      .replace(/<<<DOUBLESTAR>>>/g, '.*');
    try {
      const regex = new RegExp(`(^|/)${regexStr}($|/)`);
      return regex.test(file) || new RegExp(`^${regexStr}$`).test(file);
    } catch {
      return file.includes(pattern.replace(/\*/g, ''));
    }
  }
}
